"use client";

import { scopeProjectRef, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type {
  CmmdProject,
  CmmdProjectContextLink,
  CmmdTask,
  EnvironmentId,
} from "@cmmd-forge/contracts";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CloudIcon,
  CheckSquareIcon,
  FolderIcon,
  FolderPlusIcon,
  Loader2Icon,
  LockIcon,
  MessageSquareIcon,
  SettingsIcon,
  SquarePenIcon,
} from "lucide-react";
import { GithubIcon } from "~/components/icons/GithubIcon";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import type { WorkItem } from "./homebase/unifiedWorkItems";
import type { Project } from "../types";
import { cmmdApiClient } from "../environments/cmmdApi";
import {
  resolveCloudCmmdProjectPlacementFromBootstrap,
  resolveDefaultCloudCmmdProjectPlacement,
} from "./cloudCmmdProjectPlacement";
import { getCurrentForgeWorkspaceId } from "../workspace/currentForgeWorkspaceStore";
import { ensureProjectRepository } from "../environments/cmmdProjectRepository";
import { useCommandPaletteStore } from "../commandPaletteStore";
import { buildProjectNameMaps } from "./commandPaletteProjectMaps";
import { useProjectContentSearchPaletteActions } from "./search/useProjectContentSearchPaletteTarget";
import { readEnvironmentApi } from "../environmentApi";
import {
  buildPrimaryFetchInit,
  logoutPrimarySession,
  resolvePrimaryEnvironmentHttpUrl,
  readPrimaryEnvironmentDescriptor,
  usePrimaryEnvironmentId,
} from "../environments/primary";
import {
  ensureDesktopLocalEnvironmentConnection,
  useSavedEnvironmentRegistryStore,
} from "../environments/runtime";
import { readRuntimeSurfaceState } from "../environments/runtimeSurface";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { getGitHubRepoAccessLabel, useGitHubRepos, type GitHubRepo } from "../hooks/useGitHubRepos";
import { useSettings } from "../hooks/useSettings";
import {
  addDesktopFolderProject,
  createEmptyProjectProject,
  linkForgeProjectToCmmdProject,
  readGitHubRepositoryCloneJob,
  registerClonedProjectPath,
  startGitHubRepositoryCloneJob,
} from "../lib/addProjectSources";
import {
  isForgeAuthExpiredError,
  redirectToForgeLoginAfterAuthExpired,
} from "../lib/authExpiredRedirect";
import {
  startNewThreadInProjectFromContext,
  startNewThreadFromContext,
} from "../lib/chatThreadActions";
import { isTerminalFocused } from "../lib/terminalFocus";
import { resolveEnvModeForProject } from "~/projectThreadEnvMode";
import {
  cmmdProjectFromEnsuredGitHubContext,
  cmmdProjectFromExistingProject,
  cmmdProjectFromLinkedContext,
  githubRepoNameFromProjectTitle,
  parsePositiveCmmdId,
  type ResolvedCmmdPlanningContext,
} from "./commandPaletteCmmdProject";
import { resolveNewThreadRuntimeProjectRef } from "../lib/threadRuntimeTarget";
import { getLatestThreadForProject } from "../lib/threadSort";
import { cn, localDeviceNoun } from "../lib/utils";
import { selectProjectsAcrossEnvironments, selectSidebarThreadsAcrossEnvironments } from "../store";
import { useEnvironmentStoreSnapshot } from "../hooks/useEnvironmentStoreSnapshot";
import { selectThreadTerminalState, useTerminalStateStore } from "../terminalStateStore";
import { buildThreadRouteParams, resolveThreadRouteTarget } from "../threadRoutes";
import {
  ADDON_ICON_CLASS,
  buildAddProjectRootDescriptor,
  buildAddProjectSourceDescriptors,
  buildCmmdTaskActionItems,
  buildUncachedCmmdApiWorkItems,
  buildProjectActionItems,
  buildRootGroups,
  buildThreadActionItems,
  type CommandPaletteActionItem,
  type CommandPaletteGroup,
  type CommandPaletteSubmenuItem,
  type CommandPaletteView,
  cmmdProjectSearchText,
  filterCommandPaletteGroups,
  filterPickerRows,
  findImportedGitHubProjectForEnvironment,
  groupCmmdProjectsByWorkspace,
  getAllowedProjectImportDestinations,
  getDefaultProjectImportDestination,
  getAddProjectSourceMode,
  getAddProjectSourceIconName,
  getCommandPaletteInputPlaceholderForView,
  getCommandPaletteMode,
  getProjectSourceSubmitLabel,
  getProjectSourceSubmitState,
  ITEM_ICON_CLASS,
  RECENT_THREAD_LIMIT,
  resolveCmmdProjectCampaignName,
  type AddProjectSourceDescriptor,
} from "./CommandPalette.logic";
import {
  renderAddProjectDestinationIcon,
  renderAddProjectSourceIcon,
} from "./addProjectSourceIcons";
import { registerCloudProject } from "./registerCloudProject";
import { useCmmdWorkIntakeViewModel } from "./homebase/useCmmdWorkIntakeViewModel";
import { buildUnifiedWorkItems } from "./homebase/unifiedWorkItems";
import { CommandPaletteResults } from "./CommandPaletteResults";
import { CommandPaletteSourceList } from "./CommandPaletteSourceList";
import { CommandPaletteFooterHints } from "./CommandPaletteFooterHints";
import { CommandPaletteEmptyProjectPanel } from "./CommandPaletteEmptyProjectPanel";
import { CommandPaletteLocalFolderPanel } from "./CommandPaletteLocalFolderPanel";
import { CmmdProjectImportGroups } from "./CmmdProjectImportGroups";
import {
  addProjectSourceGroupLabel,
  buildProjectImportEnvironmentDescriptors,
  importDestinationForEnvironmentRow,
} from "./projectImportEnvironments";
import { ProjectImportDestinationPicker } from "./ProjectImportDestinationPicker";
import { useServerKeybindings } from "../rpc/serverState";
import { resolveShortcutCommand } from "../keybindings";
import {
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandInput,
  CommandPanel,
} from "./ui/command";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Kbd, KbdGroup } from "./ui/kbd";
import { toastManager } from "./ui/toast";
import { ComposerHandleContext, useComposerHandleContext } from "../composerHandleContext";
import type { ChatComposerHandle } from "./chat/ChatComposerHandle";
import { isElectron } from "../env";
import { cmmdWorkIntakeResource, useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { resolveListState } from "../lib/listViewState";
import { readCmmdForgeRuntimeListSnapshot } from "~/cmmd/cmmdForgeRuntimeListResource";
import {
  describeCloudProjectImportOutcome,
  findRegisteredCloudProjectRow,
  resolveBlankCloudImportPlan,
} from "../lib/cloudImportDedup";
import { readLocalApi } from "../localApi";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { closePrimarySidebar } from "./sidebar/primarySidebarBridge";
import { ProjectIcon } from "./ProjectIcon";
import { useProjectIconResolver } from "./projects/useProjectIconResolver";
import { useProjectDisplayNameResolver } from "./projects/useProjectDisplayNameResolver";
import { CmmdProjectImportRows } from "./CmmdProjectImportRows";
import { resolveCmmdProjectListState } from "./cmmdProjectListState";
import {
  projectImportDestinationLabel,
  blankProjectImportJobId,
  canSkipCmmdPlanningContext,
  githubProjectImportJobId,
  type ProjectImportPendingCmmdContext,
  type ProjectImportDestination,
  type ProjectImportDestinationKind,
  toProjectImportDestination,
  useProjectImportStore,
} from "../projectImportStore";

/**
 * Built once, at module scope.
 *
 * Constructing this inside the `run` handler made `no-unstable-nested-components`
 * read that handler as a component defined during render. It is not one — it
 * returns void — but the element was still allocated on every invocation, and
 * the icon never varies.
 */
const EMPTY_PROJECT_ICON = <FolderIcon className={ADDON_ICON_CLASS} />;

interface PendingCloudBlankProject {
  readonly draftName: string;
  readonly cmmdProject: {
    readonly project: CmmdProject;
    readonly organizationId: number;
    readonly campaignName: string | null;
  };
}

interface PendingGitHubCmmdContextChoice {
  readonly repo: GitHubRepo;
  readonly destination: ProjectImportDestinationKind;
}

interface CloneFromGitHubOptions {
  readonly skipCmmdContext?: boolean;
}

export function CommandPalette({ children }: { children: ReactNode }) {
  const open = useCommandPaletteStore((store) => store.open);
  const setOpen = useCommandPaletteStore((store) => store.setOpen);
  const toggleOpen = useCommandPaletteStore((store) => store.toggleOpen);
  const keybindings = useServerKeybindings();
  const composerHandleRef = useRef<ChatComposerHandle | null>(null);
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const terminalOpen = useTerminalStateStore((state) =>
    routeThreadRef
      ? selectThreadTerminalState(state.terminalStateByThreadKey, routeThreadRef).terminalOpen
      : false,
  );

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen,
        },
      });
      if (command !== "commandPalette.toggle") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      toggleOpen();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keybindings, terminalOpen, toggleOpen]);

  return (
    <ComposerHandleContext.Provider value={composerHandleRef}>
      <CommandDialog open={open} onOpenChange={setOpen}>
        {children}
        <CommandPaletteDialog />
      </CommandDialog>
    </ComposerHandleContext.Provider>
  );
}

export function ProjectSourcePanelError({ error }: { readonly error: string | null }) {
  if (!error) return null;
  return (
    <p className="mt-4 rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive">
      {error}
    </p>
  );
}

function CommandPaletteDialog() {
  const open = useCommandPaletteStore((store) => store.open);
  const setOpen = useCommandPaletteStore((store) => store.setOpen);

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  if (!open) {
    return null;
  }

  return <OpenCommandPaletteDialog />;
}

// fallow-ignore-next-line complexity
function OpenCommandPaletteDialog() {
  const navigate = useNavigate();
  const setOpen = useCommandPaletteStore((store) => store.setOpen);
  const addProjectSourcesRequested = useCommandPaletteStore(
    (store) => store.addProjectSourcesRequested,
  );
  const addProjectSourcesInitialDestination = useCommandPaletteStore(
    (store) => store.addProjectSourcesInitialDestination,
  );
  const addProjectSourcesInitialMode = useCommandPaletteStore(
    (store) => store.addProjectSourcesInitialMode,
  );
  const consumeAddProjectSourcesRequest = useCommandPaletteStore(
    (store) => store.consumeAddProjectSourcesRequest,
  );
  const composerHandleRef = useComposerHandleContext();
  const [query, setQuery] = useState("");
  const [isCreatingEmptyProject, setIsCreatingEmptyProject] = useState(false);
  const [pendingCloudBlankProject, setPendingCloudBlankProject] =
    useState<PendingCloudBlankProject | null>(null);
  const [pendingGitHubCmmdContextChoice, setPendingGitHubCmmdContextChoice] =
    useState<PendingGitHubCmmdContextChoice | null>(null);
  // Planning-context step is a two-mode segment: link an existing CMMD project,
  // or create a new one. `githubCreateProjectName` seeds the create form.
  const [githubPlanningMode, setGithubPlanningMode] = useState<"link" | "create">("link");
  const [githubCreateProjectName, setGithubCreateProjectName] = useState("");
  // Independent from the top-level repo-search `query`: the planning-context
  // step searches CMMD projects, a different domain than the GitHub repo
  // search that got the user here. Reusing `query` left it filtering the CMMD
  // list by whatever repo text was typed to find the repo in the first place.
  const [cmmdContextQuery, setCmmdContextQuery] = useState("");
  const [pendingGitHubCmmdWorkspaceId, setPendingGitHubCmmdWorkspaceId] = useState<number | null>(
    null,
  );
  const [isAddingSource, setIsAddingSource] = useState(false);
  const projectImportJobs = useProjectImportStore((store) => store.jobs);
  const activeProjectImportJobs = useMemo(
    () => projectImportJobs.filter((job) => job.status === "cloning"),
    [projectImportJobs],
  );
  const upsertProjectImportJob = useProjectImportStore((store) => store.upsertJob);
  const markProjectImportReady = useProjectImportStore((store) => store.markReady);
  const markProjectImportFailed = useProjectImportStore((store) => store.markFailed);
  const updateProjectImportProgress = useProjectImportStore((store) => store.updateProgress);
  const dismissProjectImportJob = useProjectImportStore((store) => store.dismissJob);
  const deferredQuery = useDeferredValue(query);
  const deferredCmmdContextQuery = useDeferredValue(cmmdContextQuery);
  const isActionsOnly = deferredQuery.startsWith(">");
  const settings = useSettings();
  const { activeDraftThread, activeThread, defaultProjectRef, handleNewThread } =
    useHandleNewThread();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const storeSnapshot = useEnvironmentStoreSnapshot();
  const projects = useMemo(() => selectProjectsAcrossEnvironments(storeSnapshot), [storeSnapshot]);
  const threads = useMemo(
    () => selectSidebarThreadsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const keybindings = useServerKeybindings();
  const [viewStack, setViewStack] = useState<CommandPaletteView[]>([]);
  const [projectImportDestination, setProjectImportDestination] =
    useState<ProjectImportDestination>(toProjectImportDestination("forge-cloud"));
  const [projectImportLockedDestination, setProjectImportLockedDestination] =
    useState<ProjectImportDestination | null>(null);
  const runtimeSurface = readRuntimeSurfaceState().surface;
  const currentView = viewStack.at(-1) ?? null;
  const currentProjectImportDestination =
    currentView?.importDestination ?? projectImportDestination;
  const currentProjectImportKind = currentProjectImportDestination.kind;
  const paletteMode = getCommandPaletteMode({ currentView });
  const isEmptyProjectView = currentView?.mode === "empty-project";
  const isCmmdProjectView = currentView?.mode === "cmmd-project";
  const isGitHubView = currentView?.mode === "github";
  const isGitHubCmmdContextChoiceView = isGitHubView && pendingGitHubCmmdContextChoice !== null;
  const isLocalFolderView = currentView?.mode === "local-folder";
  const cmmdIntake = useCmmdWorkIntakeViewModel();
  const {
    workspace: cmmdWorkspace,
    projects: cmmdProjects,
    campaigns: cmmdCampaigns,
    workspaces: cmmdWorkspaces,
    isLoading: cmmdProjectsLoading,
    error: cmmdProjectsError,
  } = useCmmdWorkIntakeSnapshot();
  const {
    repos: githubRepos,
    github_connected,
    github_login,
    loading: githubLoading,
    error: githubError,
    requiresCmmdAuth,
    warnings: githubWarnings,
  } = useGitHubRepos(isGitHubView ? deferredQuery : "", {
    enabled: isGitHubView || isCmmdProjectView,
  });

  const filteredCmmdProjects = useMemo(
    () =>
      filterPickerRows({
        rows: cmmdProjects,
        query: deferredQuery,
        getSearchText: cmmdProjectSearchText,
      }),
    [cmmdProjects, deferredQuery],
  );
  const filteredGithubRepos = useMemo(
    () =>
      filterPickerRows({
        rows: githubRepos,
        query: deferredQuery,
        getSearchText: (repo) =>
          [
            repo.full_name,
            repo.org_name ?? "",
            repo.access_label ?? "",
            repo.language ?? "",
            repo.description ?? "",
          ].join(" "),
      }),
    [githubRepos, deferredQuery],
  );
  const cmmdProjectWorkspaceGroups = groupCmmdProjectsByWorkspace({
    projects: filteredCmmdProjects,
    workspaces: cmmdWorkspaces,
  });
  const selectedGitHubCmmdWorkspace = useMemo(() => {
    const selectedWorkspace =
      pendingGitHubCmmdWorkspaceId !== null
        ? (cmmdWorkspaces.find(
            (workspace) => parsePositiveCmmdId(workspace.id) === pendingGitHubCmmdWorkspaceId,
          ) ?? null)
        : null;
    return selectedWorkspace ?? cmmdWorkspaces[0] ?? null;
  }, [cmmdWorkspaces, pendingGitHubCmmdWorkspaceId]);
  const filteredGitHubCmmdProjects = useMemo(
    () =>
      filterPickerRows({
        rows: cmmdProjects,
        query: deferredCmmdContextQuery,
        getSearchText: cmmdProjectSearchText,
      }),
    [cmmdProjects, deferredCmmdContextQuery],
  );
  const gitHubCmmdProjectWorkspaceGroups = useMemo(
    () =>
      groupCmmdProjectsByWorkspace({
        projects: filteredGitHubCmmdProjects,
        workspaces: cmmdWorkspaces,
      }),
    [cmmdWorkspaces, filteredGitHubCmmdProjects],
  );
  const cmmdContextListState = resolveListState({
    isLoading: cmmdProjectsLoading,
    error: cmmdProjectsError,
    rowCount: filteredGitHubCmmdProjects.length,
    emptyReason: cmmdProjects.length === 0 ? ("no-projects" as const) : ("no-match" as const),
  });
  const cmmdProjectListState = resolveCmmdProjectListState({
    isLoading: cmmdProjectsLoading,
    error: cmmdProjectsError,
    rowCount: filteredCmmdProjects.length,
    sourceCount: cmmdProjects.length,
  });
  // null for cloud: cloud-side dedup runs in registerProjectAndOpenThread after connectCloudRuntime
  const githubImportTargetEnvironmentId =
    currentProjectImportKind === "forge-cloud"
      ? null
      : (currentProjectImportDestination.environmentId ?? storeSnapshot.activeEnvironmentId);
  const importedGithubProjectByRepo = useMemo(
    () =>
      new Map(
        githubRepos.map((repo) => [
          repo.full_name,
          findImportedGitHubProjectForEnvironment(projects, repo, githubImportTargetEnvironmentId),
        ]),
      ),
    [githubImportTargetEnvironmentId, githubRepos, projects],
  );

  const { projectTitleById, projectNameByRef } = useMemo(
    () => buildProjectNameMaps(projects),
    [projects],
  );

  const activeThreadId = activeThread?.id;
  const currentProjectId = activeThread?.projectId ?? activeDraftThread?.projectId ?? null;
  /**
   * The workspace a new thread should start in for a given project.
   *
   * The project's own pin beats the app setting, so a project the palette opens
   * starts threads the same way the sidebar would. One helper rather than the
   * same lookup written at each call site.
   */
  const envModeForProject = useCallback(
    (ref: { environmentId: string; projectId: string } | null | undefined) =>
      resolveEnvModeForProject(projects, ref, settings.defaultThreadEnvMode),
    [projects, settings.defaultThreadEnvMode],
  );

  const openProjectFromSearch = useMemo(
    () => async (project: (typeof projects)[number]) => {
      const latestThread = getLatestThreadForProject(
        threads.filter((thread) => thread.environmentId === project.environmentId),
        project.id,
        settings.sidebarThreadSortOrder,
      );
      if (latestThread) {
        await navigate({
          to: "/$environmentId/$threadId",
          params: buildThreadRouteParams(
            scopeThreadRef(latestThread.environmentId, latestThread.id),
          ),
        });
        return;
      }

      const projectRef = resolveNewThreadRuntimeProjectRef({
        requestedRuntime: settings.defaultThreadRuntime,
        primaryEnvironmentId,
        projects,
        projectRef: scopeProjectRef(project.environmentId, project.id),
      });
      await handleNewThread(projectRef, {
        envMode: envModeForProject({
          environmentId: project.environmentId,
          projectId: project.id,
        }),
      });
    },
    [
      handleNewThread,
      navigate,
      primaryEnvironmentId,
      projects,
      settings.defaultThreadRuntime,
      envModeForProject,
      settings.sidebarThreadSortOrder,
      threads,
    ],
  );

  // A cloud project's icon lives on its CMMD catalog row, not on the store row
  // these lists are built from — without this the palette draws a letter avatar
  // for a project the sidebar right behind it draws properly.
  const resolveProjectIcon = useProjectIconResolver();
  const resolveProjectDisplayNameFn = useProjectDisplayNameResolver();
  const resolveProjectTitle = useCallback(
    (project: Project) => resolveProjectDisplayNameFn(project.environmentId, project.id),
    [resolveProjectDisplayNameFn],
  );

  const renderProjectIcon = useCallback(
    (project: Project) => (
      <ProjectIcon
        environmentId={project.environmentId}
        cwd={project.cwd}
        projectName={project.name}
        canonicalIdentity={resolveProjectIcon(project.environmentId, project.id)}
        className={ITEM_ICON_CLASS}
      />
    ),
    [resolveProjectIcon],
  );

  const projectSearchItems = useMemo(
    () =>
      buildProjectActionItems({
        projects,
        valuePrefix: "project",
        icon: renderProjectIcon,
        runProject: openProjectFromSearch,
        resolveDisplayName: resolveProjectTitle,
      }),
    [openProjectFromSearch, projects, renderProjectIcon, resolveProjectTitle],
  );

  const projectThreadItems = useMemo(
    () =>
      buildProjectActionItems({
        projects,
        valuePrefix: "new-thread-in",
        icon: renderProjectIcon,
        runProject: async (project) => {
          const projectRef = resolveNewThreadRuntimeProjectRef({
            requestedRuntime: settings.defaultThreadRuntime,
            primaryEnvironmentId,
            projects,
            projectRef: scopeProjectRef(project.environmentId, project.id),
          });
          await startNewThreadInProjectFromContext(
            {
              activeDraftThread,
              activeThread,
              defaultProjectRef,
              defaultThreadEnvMode: envModeForProject({
                environmentId: project.environmentId,
                projectId: project.id,
              }),
              handleNewThread,
            },
            projectRef,
          );
        },
        resolveDisplayName: resolveProjectTitle,
      }),
    [
      activeDraftThread,
      activeThread,
      defaultProjectRef,
      handleNewThread,
      primaryEnvironmentId,
      projects,
      renderProjectIcon,
      resolveProjectTitle,
      settings.defaultThreadRuntime,
      envModeForProject,
    ],
  );

  const allThreadItems = useMemo(
    () =>
      buildThreadActionItems({
        threads,
        ...(activeThreadId ? { activeThreadId } : {}),
        projectTitleById,
        sortOrder: settings.sidebarThreadSortOrder,
        icon: <MessageSquareIcon className={ITEM_ICON_CLASS} />,
        runThread: async (thread) => {
          await navigate({
            to: "/$environmentId/$threadId",
            params: buildThreadRouteParams(scopeThreadRef(thread.environmentId, thread.id)),
          });
        },
      }),
    [activeThreadId, navigate, projectTitleById, settings.sidebarThreadSortOrder, threads],
  );
  const recentThreadItems = allThreadItems.slice(0, RECENT_THREAD_LIMIT);

  const runCmmdTask = useMemo(
    () => async (task: WorkItem) => {
      if (task.threadId && task.environmentId) {
        await navigate({
          to: "/$environmentId/$threadId",
          params: buildThreadRouteParams(scopeThreadRef(task.environmentId, task.threadId)),
        });
      } else {
        await navigate({ to: "/", search: { task: task.id } });
      }
    },
    [navigate],
  );

  const cmmdTaskSearchItems = useMemo(
    () =>
      buildCmmdTaskActionItems({
        tasks: buildUnifiedWorkItems(cmmdIntake.taskRows, threads, projectNameByRef).filter(
          (item) => item.source === "cmmd-task",
        ),
        icon: <CheckSquareIcon className={ITEM_ICON_CLASS} />,
        runTask: runCmmdTask,
      }),
    [cmmdIntake.taskRows, projectNameByRef, runCmmdTask, threads],
  );

  // Live API search — finds tasks outside the work-intake cache when query ≥ 2 chars.
  const [apiCmmdTasks, setApiCmmdTasks] = useState<ReadonlyArray<CmmdTask>>([]);

  useEffect(() => {
    const q = deferredQuery.trim();
    if (q.length < 2 || q.startsWith(">") || currentView !== null) {
      setApiCmmdTasks([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      cmmdApiClient
        .searchTasks({ q, limit: 8 })
        .then((tasks) => {
          if (!cancelled) setApiCmmdTasks(tasks as CmmdTask[]);
        })
        .catch(() => {
          // Silently degrade — cached results still shown
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [deferredQuery, currentView]);

  // Merge API results with cached items, keeping only tasks absent from the cache.
  const cachedTaskValueSet = useMemo(
    () => new Set(cmmdTaskSearchItems.map((item) => item.value)),
    [cmmdTaskSearchItems],
  );

  const apiOnlyCmmdTaskItems = useMemo(() => {
    const uncached = buildUncachedCmmdApiWorkItems({
      apiTasks: apiCmmdTasks,
      cachedTaskValueSet,
      fallbackActivityAt: new Date().toISOString(),
    });
    if (uncached.length === 0) return [];
    return buildCmmdTaskActionItems({
      tasks: uncached,
      icon: <CheckSquareIcon className={ITEM_ICON_CLASS} />,
      runTask: runCmmdTask,
    });
  }, [apiCmmdTasks, cachedTaskValueSet, runCmmdTask]);

  const mergedCmmdTaskSearchItems = useMemo(
    () => [...cmmdTaskSearchItems, ...apiOnlyCmmdTaskItems],
    [cmmdTaskSearchItems, apiOnlyCmmdTaskItems],
  );

  function pushView(item: CommandPaletteSubmenuItem): void {
    setViewStack((previousViews) => [
      ...previousViews,
      {
        addonIcon: item.addonIcon,
        groups: item.groups,
        ...(item.initialQuery ? { initialQuery: item.initialQuery } : {}),
      },
    ]);
    setQuery(item.initialQuery ?? "");
  }

  function pushAddProjectSourcesView(
    initialDestination: ProjectImportDestination | null = null,
    push = false,
  ): void {
    const destination = initialDestination ?? toProjectImportDestination("forge-cloud");
    const scopedItems = buildAddProjectSourceDescriptors(
      initialDestination?.kind ?? null,
      runtimeSurface,
    ).map((source) => buildAddProjectSourceItem(source, initialDestination));
    const groupLabel = addProjectSourceGroupLabel(initialDestination?.kind ?? null);
    const sourcesView: CommandPaletteView = {
      addonIcon: <FolderPlusIcon className={ADDON_ICON_CLASS} />,
      groups: [{ value: "sources", label: groupLabel, items: scopedItems }],
    };
    // `push` keeps the destination step underneath so Backspace returns to it.
    // The scoped entry points (sidebar Cloud/Local "+") replace instead: they
    // already answered "where", so there is nothing to go back to.
    setViewStack((previousViews) => (push ? [...previousViews, sourcesView] : [sourcesView]));
    setProjectImportDestination(destination);
    setProjectImportLockedDestination(initialDestination);
    setPendingCloudBlankProject(null);
    setPendingGitHubCmmdContextChoice(null);
    setQuery("");
    setCmmdContextQuery("");
  }

  /**
   * "Where" step. Import destination is a property of the workspace, not of the
   * source it came from, so it is asked first and every later step is scoped to
   * it — which is also what makes the device-only Folder source disappear on
   * cloud without a special case. Skipped when only one destination is possible
   * (e.g. the cloud-official surface), so a single-runtime user never sees a
   * one-option menu.
   */
  function openAddProjectFlow(): void {
    // One destination means nothing to ask, so open straight onto its sources.
    if (onlyAddProjectDestination) {
      pushAddProjectSourcesView(importDestinationForEnvironmentRow(onlyAddProjectDestination));
      return;
    }
    setViewStack([
      {
        addonIcon: <FolderPlusIcon className={ADDON_ICON_CLASS} />,
        // Same items the root "Add project" submenu renders, so the two entry
        // points cannot drift apart.
        groups: [addProjectDestinationGroup],
      },
    ]);
    setProjectImportDestination(
      toProjectImportDestination(getDefaultProjectImportDestination("empty-project")),
    );
    setProjectImportLockedDestination(null);
    setPendingCloudBlankProject(null);
    setPendingGitHubCmmdContextChoice(null);
    setQuery("");
    setCmmdContextQuery("");
  }

  function pushAddProjectSourceView(
    mode: NonNullable<CommandPaletteView["mode"]>,
    addonIcon: ReactNode,
    lockedDestination: ProjectImportDestination | null = null,
  ): void {
    if (mode === "cmmd-project") {
      cmmdWorkIntakeResource.requestRefetch({ authoritative: true });
    }
    const destination =
      lockedDestination ?? toProjectImportDestination(getDefaultProjectImportDestination(mode));
    setProjectImportDestination(destination);
    setProjectImportLockedDestination(lockedDestination);
    setViewStack((previousViews) => [
      ...previousViews,
      {
        mode,
        importDestination: destination,
        addonIcon,
        groups: [],
      },
    ]);
    setPendingCloudBlankProject(null);
    setPendingGitHubCmmdContextChoice(null);
    setQuery("");
    setCmmdContextQuery("");
  }

  // The picker answers "cloud or this device", not "which machine", so a toggle
  // clears any Environment the "where" step had already named.
  function selectProjectImportDestination(kind: ProjectImportDestinationKind): void {
    const destination = toProjectImportDestination(kind);
    setProjectImportDestination(destination);
    setViewStack((previousViews) =>
      previousViews.map((view, index) =>
        index === previousViews.length - 1 ? { ...view, importDestination: destination } : view,
      ),
    );
  }

  function popView(): void {
    setViewStack((previousViews) => previousViews.slice(0, -1));
    setPendingCloudBlankProject(null);
    setPendingGitHubCmmdContextChoice(null);
    setQuery("");
    setCmmdContextQuery("");
  }

  function handleQueryChange(nextQuery: string): void {
    if (isGitHubCmmdContextChoiceView) {
      // The planning-context step searches CMMD projects, not GitHub repos —
      // keep it off the shared `query` state so it can never inherit a
      // leftover repo-search string (see `cmmdContextQuery` above).
      setCmmdContextQuery(nextQuery);
      return;
    }
    setQuery(nextQuery);
    if (isEmptyProjectView) {
      if (pendingCloudBlankProject && pendingCloudBlankProject.draftName !== nextQuery.trim()) {
        setPendingCloudBlankProject(null);
      }
    }
    if (isCmmdProjectView || isGitHubView || isLocalFolderView) {
    }
    if (nextQuery === "" && currentView?.initialQuery) {
      popView();
    }
  }

  const projectSourceSubmitState = getProjectSourceSubmitState({
    currentView,
    query,
    isCreatingEmptyProject,
    isAddingSource,
  });

  async function resolveCloudCmmdProjectPlacement(): Promise<{
    readonly organizationId: number;
    readonly campaignId: number | null;
    readonly workspaceName: string;
    readonly campaignName: string | null;
  }> {
    try {
      return resolveDefaultCloudCmmdProjectPlacement({
        currentWorkspaceId: getCurrentForgeWorkspaceId(),
        workspace: cmmdWorkspace,
        workspaces: cmmdWorkspaces,
        campaigns: cmmdCampaigns,
      });
    } catch {
      return resolveCloudCmmdProjectPlacementFromBootstrap();
    }
  }

  async function createCmmdControlPlaneProjectForCloud(name: string): Promise<{
    readonly project: CmmdProject;
    readonly organizationId: number;
    readonly campaignName: string | null;
  }> {
    const placement = await resolveCloudCmmdProjectPlacement();
    const created = await cmmdApiClient.createCmmdProject({
      name,
      ...(placement.campaignId !== null ? { campaignId: placement.campaignId } : {}),
      organizationId: placement.organizationId,
    });
    const cmmdProjectId = parsePositiveCmmdId(created.id);
    const cmmdProjectName =
      typeof created.name === "string" && created.name.trim().length > 0
        ? created.name.trim()
        : name;
    if (!cmmdProjectId) {
      throw new Error("CMMD created the project without returning a project id.");
    }
    return {
      organizationId: placement.organizationId,
      campaignName: placement.campaignName,
      project: {
        id: String(cmmdProjectId),
        title: cmmdProjectName,
        status: "draft",
        campaignId: placement.campaignId !== null ? String(placement.campaignId) : null,
        workspaceId: String(placement.organizationId),
        workspaceName: placement.workspaceName,
        teamId: null,
        teamName: null,
        description: null,
        brief: null,
      },
    };
  }

  async function ensureCmmdControlPlaneProjectForCloudGitHubRepo(
    repoFullName: string,
    name: string,
  ): Promise<{
    readonly project: CmmdProject;
    readonly organizationId: number;
    readonly campaignName: string | null;
  }> {
    const placement = await resolveCloudCmmdProjectPlacement();
    const ensured = await cmmdApiClient.ensureGitHubProjectContext({
      githubRepoFullName: repoFullName,
      projectName: name,
      cmmdOrganizationId: placement.organizationId,
      ...(placement.campaignId !== null ? { campaignId: placement.campaignId } : {}),
    });
    return cmmdProjectFromEnsuredGitHubContext(
      ensured,
      placement.organizationId,
      placement.workspaceName,
      placement.campaignName,
      name,
    );
  }

  async function resolveCmmdControlPlaneProjectForCloudGitHubRepo(
    repoFullName: string,
    jobId: string,
    selectedContext: ResolvedCmmdPlanningContext | null,
    knownContexts?: readonly CmmdProjectContextLink[],
    pendingContext?: ProjectImportPendingCmmdContext | null,
  ): Promise<{
    readonly project: CmmdProject;
    readonly organizationId: number;
    readonly campaignName: string | null;
    readonly source: "linked_existing" | "created_from_cmmd";
  }> {
    if (selectedContext) {
      return selectedContext;
    }

    const linkedContexts =
      knownContexts ?? (await listCmmdProjectContextLinksForGitHubRepo(repoFullName));
    const linkedContext = linkedContexts[0] ?? null;
    if (linkedContext) {
      return cmmdProjectFromLinkedContext(linkedContext);
    }

    if (pendingContext?.repoFullName.toLowerCase() === repoFullName.toLowerCase()) {
      return pendingContext;
    }

    const repoName = repoFullName.split("/").at(-1)?.trim() || repoFullName;
    const created = await ensureCmmdControlPlaneProjectForCloudGitHubRepo(repoFullName, repoName);
    const context = { repoFullName, ...created, source: "created_from_cmmd" as const };
    upsertProjectImportJob({
      id: jobId,
      source: "github",
      destination: "forge-cloud",
      label: repoFullName,
      detail: "Created CMMD control-plane project",
      pendingCmmdContext: context,
    });
    return context;
  }

  async function addProjectBaseForDestination(destination: ProjectImportDestination) {
    if (destination.kind === "forge-cloud") {
      throw new Error("Forge Cloud imports require a registered CMMD project.");
    }
    return addLocalProjectBase(destination.environmentId);
  }

  async function addLocalProjectBase(chosenEnvironmentId: EnvironmentId | null = null) {
    const runtimeSurface = readRuntimeSurfaceState();
    if (runtimeSurface.executionRuntime === "cloud" && runtimeSurface.surface !== "desktop-cloud") {
      throw new Error(
        "Switch to Desktop Local before importing local projects. Cloud projects execute inside the Forge Cloud runtime.",
      );
    }
    const localConnection =
      runtimeSurface.surface === "desktop-cloud"
        ? await ensureDesktopLocalEnvironmentConnection()
        : null;
    // The "where" step names a machine, so its answer outranks whichever local
    // runtime happens to be connected.
    const localEnvironmentId =
      chosenEnvironmentId ?? localConnection?.environmentId ?? primaryEnvironmentId;
    const api = localEnvironmentId ? readEnvironmentApi(localEnvironmentId) : null;
    if (!localEnvironmentId || !api) {
      throw new Error(
        "Forge needs an active Desktop Local runtime before it can add local projects.",
      );
    }
    const bootstrapEnvelope = window.desktopBridge?.getLocalEnvironmentBootstrap() ?? null;
    const localBearerToken =
      bootstrapEnvelope?.primaryMode === "cloud"
        ? bootstrapEnvelope.desktopLocal?.bearerToken
        : bootstrapEnvelope?.bearerToken;
    const localHttpBaseUrl = localConnection?.knownEnvironment.target.httpBaseUrl ?? null;
    const resolveLocalHttpUrl = (path: string, searchParams?: Record<string, string>) => {
      if (!localHttpBaseUrl) {
        return resolvePrimaryEnvironmentHttpUrl(path, searchParams);
      }
      const url = new URL(localHttpBaseUrl);
      const basePath = url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
      url.pathname = `${basePath}${path.startsWith("/") ? path : `/${path}`}`;
      url.search = searchParams ? new URLSearchParams(searchParams).toString() : "";
      url.hash = "";
      return url.toString();
    };
    return {
      api,
      environmentId: localEnvironmentId,
      projects,
      defaultThreadEnvMode: settings.defaultThreadEnvMode,
      handleNewThread,
      resolveHttpUrl: resolveLocalHttpUrl,
      fetchImpl: (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
        const headers = new Headers(init?.headers);
        if (localBearerToken && !headers.has("authorization")) {
          headers.set("authorization", `Bearer ${localBearerToken}`);
        }
        return fetch(input, { ...init, headers });
      },
    };
  }

  async function createEmptyProject(destination = currentProjectImportDestination): Promise<void> {
    const name = query.trim();
    if (!name || isCreatingEmptyProject) return;
    const jobId = blankProjectImportJobId(name, destination.kind);
    if (activeProjectImportJobs.some((job) => job.id === jobId)) return;

    // CMMD's project create endpoint has no name uniqueness, so a repeat
    // create (retry after a reload, or a case variant like "CMMD" vs "cmmd")
    // would mint another control-plane project + runtime and a permanent
    // duplicate CLOUD sidebar row. The plan resumes a prior attempt's CMMD
    // project when one exists, and blocks when a live cloud project already
    // answers to this name.
    const cloudImportPlan = resolveBlankCloudImportPlan({
      cloudDestination: destination.kind === "forge-cloud",
      name,
      jobId,
      draftPending: pendingCloudBlankProject,
      jobs: projectImportJobs,
      rows: readCmmdForgeRuntimeListSnapshot().rows,
    });
    if (cloudImportPlan.kind === "block") {
      // Blocked, not broken: the workspace exists and is healthy, so this is
      // `info`. Destructive styling has to stay rare to keep meaning anything.
      toastManager.add({
        type: "info",
        title: cloudImportPlan.notice.title,
        description: cloudImportPlan.notice.detail,
      });
      return;
    }

    setProjectImportDestination(destination);
    setIsCreatingEmptyProject(true);
    upsertProjectImportJob({
      id: jobId,
      source: "blank",
      destination: destination.kind,
      label: name,
      detail:
        destination.kind === "forge-cloud"
          ? "Preparing Forge Cloud project"
          : "Preparing project on this device",
    });
    // Deliberately NOT closing here. The cloud branch used to close before its
    // work began, which threw away the only progress the user could see: the
    // palette vanished, then a row appeared in the sidebar a beat later, and
    // whether anything had happened was left to inference. The submit control
    // reports "Adding" instead, and the palette closes once the work resolves.
    try {
      if (destination.kind === "forge-cloud") {
        let resolvedCmmdProject =
          cloudImportPlan.kind === "resume" ? cloudImportPlan.cmmdProject : null;
        if (!resolvedCmmdProject) {
          updateProjectImportProgress(jobId, {
            detail: "Creating CMMD control-plane project",
            phase: "CMMD project",
            percent: 20,
          });
          resolvedCmmdProject = await createCmmdControlPlaneProjectForCloud(name);
          // Persist the created project on the job so a retry after a
          // mid-flow failure (VM provisioning, workspace create) reuses it
          // instead of creating a duplicate — even across a reload.
          // repoFullName is empty: blank jobs have no repo, and recovery
          // matches on the job id, never on this field.
          upsertProjectImportJob({
            id: jobId,
            source: "blank",
            destination: destination.kind,
            label: name,
            detail: "Created CMMD control-plane project",
            pendingCmmdContext: {
              repoFullName: "",
              project: resolvedCmmdProject.project,
              organizationId: resolvedCmmdProject.organizationId,
              campaignName: resolvedCmmdProject.campaignName,
              source: "created_from_cmmd",
            },
          });
        }
        setPendingCloudBlankProject({
          draftName: name,
          cmmdProject: resolvedCmmdProject,
        });
        const cmmdProjectId = parsePositiveCmmdId(resolvedCmmdProject.project.id);
        if (cmmdProjectId === null) {
          throw new Error("CMMD returned a project without a usable id.");
        }
        updateProjectImportProgress(jobId, {
          detail: "Adding the project to Forge Cloud",
          phase: "Forge Cloud",
          percent: 80,
        });
        // Register only. The VM and its workspace directory are created on the
        // first thread, so this returns in well under a second.
        await registerCloudProject({ cmmdProjectId });
        markProjectImportReady(jobId);
        window.setTimeout(() => dismissProjectImportJob(jobId), 4_000);
        setPendingCloudBlankProject(null);
        setOpen(false);
        closePrimarySidebar();
        return;
      }
      updateProjectImportProgress(jobId, {
        detail: "Connecting to Desktop Local runtime",
        phase: "This device",
        percent: 45,
      });
      const localProjectBase = await addLocalProjectBase(destination.environmentId);
      updateProjectImportProgress(jobId, {
        detail: "Creating local project",
        phase: "Workspace",
        percent: 80,
      });
      await createEmptyProjectProject({
        ...localProjectBase,
        name,
      });
      markProjectImportReady(jobId);
      window.setTimeout(() => dismissProjectImportJob(jobId), 4_000);
      setPendingCloudBlankProject(null);
      setOpen(false);
      closePrimarySidebar();
    } catch (error) {
      if (isForgeAuthExpiredError(error)) {
        markProjectImportFailed(jobId, "Forge sign-in expired. Sign in again, then retry.");
        // The palette no longer closes up front, so the one path that navigates
        // away has to close it itself — otherwise it would hang over the login
        // screen the redirect is about to show.
        setOpen(false);
        void redirectToForgeLoginAfterAuthExpired();
        return;
      }
      markProjectImportFailed(
        jobId,
        error instanceof Error ? error.message : "Failed to create blank project.",
      );
      toastManager.add({
        type: "error",
        title: "Could not create the project",
        description: error instanceof Error ? error.message : "Failed to create blank project.",
      });
    } finally {
      setIsCreatingEmptyProject(false);
    }
  }

  async function runAddProjectSource(operation: () => Promise<void>): Promise<void> {
    if (isAddingSource) return;
    setIsAddingSource(true);
    try {
      await operation();
      setOpen(false);
      closePrimarySidebar();
    } catch (error) {
      if (isForgeAuthExpiredError(error)) {
        void redirectToForgeLoginAfterAuthExpired();
        return;
      }
      reportSourceOutcome(
        "error",
        "Couldn't add the project",
        error instanceof Error ? error.message : "Failed to add project.",
      );
    } finally {
      setIsAddingSource(false);
    }
  }

  // fallow-ignore-next-line complexity
  async function createFromCmmdProject(project: (typeof cmmdProjects)[number]): Promise<void> {
    const campaignName = resolveCmmdProjectCampaignName(project, cmmdCampaigns);
    const projectId = parsePositiveCmmdId(project.id);
    const organizationId =
      parsePositiveCmmdId(project.workspaceId) ?? parsePositiveCmmdId(cmmdWorkspace?.id);
    let repoOwner = github_login?.trim() ?? "";
    if (!projectId || !organizationId) {
      reportSourceOutcome(
        "error",
        "CMMD project identity is incomplete",
        "Refresh CMMD, then try again.",
      );
      return;
    }
    if (!repoOwner) {
      // github_login is populated asynchronously by useGitHubRepos (fetchStatus runs
      // after the palette opens, on a SEPARATE request from github_connected), so it
      // can still be null the instant the user clicks — which wrongly blocked import
      // with "Connect GitHub" even when GitHub is fully connected. Resolve it live.
      repoOwner = await resolveGithubLoginNow();
    }
    if (!repoOwner) {
      reportSourceOutcome(
        "blocked",
        "Connect GitHub first",
        "A CMMD project import needs a GitHub account to create its repository.",
      );
      return;
    }
    const repoName = githubRepoNameFromProjectTitle(project.title, String(projectId));
    const destination = currentProjectImportKind;
    const repoFullName = `${repoOwner}/${repoName}`;
    const jobId = githubProjectImportJobId(repoFullName, destination);
    if (activeProjectImportJobs.some((job) => job.id === jobId)) return;

    upsertProjectImportJob({
      id: jobId,
      source: "github",
      destination,
      label: repoFullName,
      detail:
        destination === "forge-cloud"
          ? "Ensuring CMMD project repository"
          : `Preparing checkout on ${localDeviceNoun()}`,
    });
    setOpen(false);

    // fallow-ignore-next-line complexity
    void (async () => {
      try {
        const ensured = await ensureProjectRepository({
          projectId,
          cmmdOrganizationId: organizationId,
          owner: repoOwner,
          name: repoName,
          visibility: "private",
          description: `Forge execution repository for ${project.title}`,
          autoInit: true,
        });
        const ensuredRepoFullName = ensured.repository.githubRepoFullName;
        if (destination === "forge-cloud") {
          updateProjectImportProgress(jobId, {
            detail: "Adding the project to Forge Cloud",
            phase: "Forge Cloud",
            percent: 80,
          });
          // Register only. CMMD already knows the repo (ensureProjectRepository
          // wrote `projects.github_repo_url`), so the clone happens inside the
          // sprite on the first thread rather than blocking the import.
          await registerCloudProject({ cmmdProjectId: projectId });
          markProjectImportReady(jobId);
          window.setTimeout(() => dismissProjectImportJob(jobId), 4_000);
          return;
        }
        const baseInput = await addProjectBaseForDestination(currentProjectImportDestination);
        const firstSnapshot = await startGitHubRepositoryCloneJob({
          ...baseInput,
          repoFullName: ensuredRepoFullName,
        });
        let snapshot = firstSnapshot;
        updateProjectImportProgress(jobId, {
          detail: snapshot.detail,
          phase: snapshot.phase,
          percent: snapshot.percent,
          current: snapshot.current,
          total: snapshot.total,
        });
        while (snapshot.status === "queued" || snapshot.status === "running") {
          await new Promise((resolve) => window.setTimeout(resolve, 750));
          snapshot = await readGitHubRepositoryCloneJob({
            ...baseInput,
            jobId: snapshot.id,
          });
          updateProjectImportProgress(jobId, {
            detail: snapshot.detail,
            phase: snapshot.phase,
            percent: snapshot.percent,
            current: snapshot.current,
            total: snapshot.total,
          });
        }
        if (snapshot.status === "failed") {
          throw new Error(snapshot.error ?? snapshot.detail);
        }
        if (!snapshot.local_path) {
          throw new Error("GitHub clone finished without a local project path.");
        }
        const result = await registerClonedProjectPath(
          {
            ...baseInput,
            focusMostRecentThreadForProject: async (projectRef) => {
              const importedProject = projects.find(
                (candidate) =>
                  candidate.environmentId === projectRef.environmentId &&
                  candidate.id === projectRef.projectId,
              );
              if (importedProject) {
                await openProjectFromSearch(importedProject);
              }
            },
          },
          snapshot.local_path,
        );
        await linkForgeProjectToCmmdProject({
          ...baseInput,
          cwd: snapshot.local_path,
          projectId: result.projectId,
          project,
          campaignName,
          githubRepoFullName: ensuredRepoFullName,
          source: ensured.created ? "created_from_cmmd" : "linked_existing",
          cmmdOrganizationId: organizationId,
        });
        upsertProjectImportJob({
          id: jobId,
          source: "github",
          destination,
          label: ensuredRepoFullName,
          detail: "Project ready",
          pendingCmmdContext: null,
        });
        markProjectImportReady(jobId);
        window.setTimeout(() => dismissProjectImportJob(jobId), 4_000);
        toastManager.add({
          type: "success",
          title: result.existing || snapshot.existing ? "Opened existing project" : "Project ready",
          description: `${ensuredRepoFullName} is ready for threads.`,
        });
      } catch (error) {
        if (isForgeAuthExpiredError(error)) {
          markProjectImportFailed(jobId, "Forge sign-in expired. Sign in again, then retry.");
          void redirectToForgeLoginAfterAuthExpired();
          return;
        }
        markProjectImportFailed(
          jobId,
          error instanceof Error ? error.message : "Failed to import CMMD project.",
        );
      }
    })();
  }

  async function cloneFromGitHub(
    repo: GitHubRepo,
    selectedCmmdContext: ResolvedCmmdPlanningContext | null = null,
    options: CloneFromGitHubOptions = {},
  ): Promise<void> {
    const destination = currentProjectImportKind;
    const jobId = githubProjectImportJobId(repo.full_name, destination);
    if (activeProjectImportJobs.some((job) => job.id === jobId)) return;
    const importedProject = importedGithubProjectByRepo.get(repo.full_name) ?? null;
    if (importedProject) {
      await openProjectFromSearch(importedProject);
      toastManager.add({
        type: "success",
        title: "Opened existing project",
        description: `${repo.full_name} is already imported.`,
      });
      setOpen(false);
      closePrimarySidebar();
      return;
    }

    if (destination === "forge-cloud" && !selectedCmmdContext && !options.skipCmmdContext) {
      setPendingGitHubCmmdContextChoice({ repo, destination });
      setPendingGitHubCmmdWorkspaceId(parsePositiveCmmdId(cmmdWorkspace?.id));
      setCmmdContextQuery("");
      return;
    }

    upsertProjectImportJob({
      id: jobId,
      source: "github",
      destination,
      label: repo.full_name,
      detail:
        destination === "forge-cloud"
          ? "Starting cloud VM and preparing checkout"
          : `Preparing checkout on ${localDeviceNoun()}`,
    });
    setOpen(false);

    void (async () => {
      try {
        const existingImportJob = projectImportJobs.find((job) => job.id === jobId) ?? null;
        const cmmdContext =
          destination === "forge-cloud" && !options.skipCmmdContext
            ? await resolveCmmdControlPlaneProjectForCloudGitHubRepo(
                repo.full_name,
                jobId,
                selectedCmmdContext,
                undefined,
                existingImportJob?.pendingCmmdContext ?? null,
              )
            : null;
        if (destination === "forge-cloud" && cmmdContext) {
          updateProjectImportProgress(jobId, {
            detail: "Adding the project to Forge Cloud",
            phase: "Forge Cloud",
            percent: 80,
          });
          // Read the catalog BEFORE registering: afterwards every project looks
          // registered, and the difference between a first import and a no-op
          // re-import is exactly what the finished message has to be honest
          // about. Register only — the repo is recorded on the CMMD project, and
          // the sprite that clones it is created on the first thread.
          const priorRow = findRegisteredCloudProjectRow(
            readCmmdForgeRuntimeListSnapshot().rows,
            Number(cmmdContext.project.id),
          );
          await registerCloudProject({ cmmdProjectId: Number(cmmdContext.project.id) });
          upsertProjectImportJob({
            id: jobId,
            source: "github",
            destination,
            label: repo.full_name,
            // Registration is not a rebuild. Saying "Project ready" here told
            // a user whose workspace was broken that re-importing had fixed it,
            // when the call provably cannot: it writes a registration row and
            // returns without provisioning, connecting, materializing the
            // workspace, or rewriting the CMMD project link.
            detail: describeCloudProjectImportOutcome({
              alreadyRegistered: priorRow !== null,
              hasRuntime: priorRow?.runtimeId != null,
            }),
            pendingCmmdContext: null,
          });
          markProjectImportReady(jobId);
          window.setTimeout(() => dismissProjectImportJob(jobId), 8_000);
          return;
        }
        const baseInput = await addProjectBaseForDestination(currentProjectImportDestination);
        const firstSnapshot = await startGitHubRepositoryCloneJob({
          ...baseInput,
          repoFullName: repo.full_name,
        });
        let snapshot = firstSnapshot;
        updateProjectImportProgress(jobId, {
          detail: snapshot.detail,
          phase: snapshot.phase,
          percent: snapshot.percent,
          current: snapshot.current,
          total: snapshot.total,
        });
        while (snapshot.status === "queued" || snapshot.status === "running") {
          await new Promise((resolve) => window.setTimeout(resolve, 750));
          snapshot = await readGitHubRepositoryCloneJob({
            ...baseInput,
            jobId: snapshot.id,
          });
          updateProjectImportProgress(jobId, {
            detail: snapshot.detail,
            phase: snapshot.phase,
            percent: snapshot.percent,
            current: snapshot.current,
            total: snapshot.total,
          });
        }
        if (snapshot.status === "failed") {
          throw new Error(snapshot.error ?? snapshot.detail);
        }
        if (!snapshot.local_path) {
          throw new Error("GitHub clone finished without a local project path.");
        }
        const result = await registerClonedProjectPath(
          {
            ...baseInput,
            focusMostRecentThreadForProject: async (projectRef) => {
              const project = projects.find(
                (candidate) =>
                  candidate.environmentId === projectRef.environmentId &&
                  candidate.id === projectRef.projectId,
              );
              if (project) {
                await openProjectFromSearch(project);
              }
            },
          },
          snapshot.local_path,
        );
        if (cmmdContext) {
          await linkForgeProjectToCmmdProject({
            ...baseInput,
            cwd: snapshot.local_path,
            projectId: result.projectId,
            project: cmmdContext.project,
            campaignName: cmmdContext.campaignName,
            githubRepoFullName: repo.full_name,
            source: cmmdContext.source,
          });
        }
        upsertProjectImportJob({
          id: jobId,
          source: "github",
          destination,
          label: repo.full_name,
          detail: "Project ready",
          pendingCmmdContext: null,
        });
        setPendingGitHubCmmdContextChoice(null);
        setPendingGitHubCmmdWorkspaceId(null);
        setCmmdContextQuery("");
        markProjectImportReady(jobId);
        window.setTimeout(() => dismissProjectImportJob(jobId), 4_000);
        if (result.existing || snapshot.existing) {
          toastManager.add({
            type: "success",
            title: "Opened existing project",
            description: `${repo.full_name} was already imported.`,
          });
        } else {
          toastManager.add({
            type: "success",
            title: "Project ready",
            description: `${repo.full_name} finished cloning and is ready for threads.`,
          });
        }
      } catch (error) {
        if (isForgeAuthExpiredError(error)) {
          markProjectImportFailed(jobId, "Forge sign-in expired. Sign in again, then retry.");
          void redirectToForgeLoginAfterAuthExpired();
          return;
        }
        markProjectImportFailed(
          jobId,
          error instanceof Error ? error.message : "Failed to clone GitHub repository.",
        );
      }
    })();
  }

  function cloneFromGitHubExistingCmmdProject(
    repo: GitHubRepo,
    project: (typeof cmmdProjects)[number],
  ): Promise<void> {
    const workspace =
      cmmdWorkspaces.find(
        (candidate) =>
          parsePositiveCmmdId(candidate.id) === parsePositiveCmmdId(project.workspaceId),
      ) ?? null;
    const campaignName = resolveCmmdProjectCampaignName(project, cmmdCampaigns);
    const fallbackOrganizationId =
      parsePositiveCmmdId(selectedGitHubCmmdWorkspace?.id) ??
      parsePositiveCmmdId(cmmdWorkspace?.id) ??
      null;
    const context = cmmdProjectFromExistingProject(
      project,
      workspace?.name ?? project.workspaceName ?? "CMMD",
      campaignName,
      fallbackOrganizationId,
    );
    if (!context) {
      reportSourceOutcome(
        "blocked",
        "Pick a CMMD workspace",
        "This project has no project resolved yet. Choose one below, then try again.",
      );
      return Promise.resolve();
    }
    return cloneFromGitHub(repo, context);
  }

  function handleGitHubRepoSelected(repo: GitHubRepo): void {
    if (currentProjectImportKind !== "forge-cloud") {
      void cloneFromGitHub(repo);
      return;
    }
    cmmdWorkIntakeResource.requestRefetch({ authoritative: true });
    setPendingGitHubCmmdContextChoice({ repo, destination: currentProjectImportKind });
    setPendingGitHubCmmdWorkspaceId(parsePositiveCmmdId(cmmdWorkspace?.id));
    setGithubPlanningMode("link");
    setGithubCreateProjectName(repo.full_name.split("/").at(-1)?.trim() || repo.full_name);
    // The repo-search text that found this repo is a different search domain
    // than the CMMD project list about to render — never carry it over
    // (see `cmmdContextQuery`).
    setCmmdContextQuery("");
  }

  async function createGitHubCmmdProjectAndClone(repo: GitHubRepo): Promise<void> {
    const workspace = selectedGitHubCmmdWorkspace;
    const organizationId = parsePositiveCmmdId(workspace?.id);
    if (!organizationId) {
      reportSourceOutcome(
        "blocked",
        "Pick a CMMD workspace",
        "A planning project is created inside a project.",
      );
      return;
    }
    const repoName = repo.full_name.split("/").at(-1)?.trim() || repo.full_name;
    const projectName =
      githubCreateProjectName.trim().length > 0 ? githubCreateProjectName.trim() : repoName;
    setIsAddingSource(true);
    try {
      const ensured = await cmmdApiClient.ensureGitHubProjectContext({
        githubRepoFullName: repo.full_name,
        projectName,
        cmmdOrganizationId: organizationId,
      });
      await cloneFromGitHub(
        repo,
        cmmdProjectFromEnsuredGitHubContext(
          ensured,
          organizationId,
          workspace?.name ?? "CMMD",
          null,
          projectName,
        ),
      );
    } catch (error) {
      reportSourceOutcome(
        "error",
        "Couldn't create the planning project",
        error instanceof Error ? error.message : "Failed to create CMMD planning project.",
      );
    } finally {
      setIsAddingSource(false);
    }
  }

  async function connectGitHub(): Promise<void> {
    try {
      const response = await fetch(
        resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/authorize-url", {
          // Pass our own origin so CMMD's callback returns the user to Forge
          // (allow-listed server-side) instead of landing them on the CMMD site.
          return_to: window.location.origin,
        }),
        buildPrimaryFetchInit({ credentials: "include" }),
      );
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `GitHub auth unavailable (HTTP ${response.status})`);
      }
      const data = (await response.json()) as { url?: string };
      if (!data.url) throw new Error("GitHub auth URL was not returned.");
      window.location.href = data.url;
    } catch (error) {
      reportSourceOutcome(
        "error",
        "GitHub sign-in unavailable",
        error instanceof Error ? error.message : "GitHub auth unavailable.",
      );
    }
  }

  async function addFromLocalFolder(): Promise<void> {
    await runAddProjectSource(async () => {
      const result = await addDesktopFolderProject({
        ...(await addLocalProjectBase()),
        localApi: isElectron ? readLocalApi() : null,
      });
      if ("cancelled" in result) {
        throw new Error("Local folders require Forge Desktop.");
      }
    });
  }

  const actionItems: Array<CommandPaletteActionItem | CommandPaletteSubmenuItem> =
    useProjectContentSearchPaletteActions({
      activeThread,
      icon: <FolderIcon className={ITEM_ICON_CLASS} />,
      projects,
      threads,
    });

  if (projects.length > 0) {
    const activeProjectTitle = currentProjectId
      ? (projectTitleById.get(currentProjectId) ?? null)
      : null;

    if (activeProjectTitle) {
      actionItems.push({
        kind: "action",
        value: "action:new-thread",
        searchTerms: ["new thread", "chat", "create", "draft"],
        title: (
          <>
            New thread in <span className="font-semibold">{activeProjectTitle}</span>
          </>
        ),
        icon: <SquarePenIcon className={ITEM_ICON_CLASS} />,
        shortcutCommand: "chat.new",
        run: async () => {
          await startNewThreadFromContext({
            activeDraftThread,
            activeThread,
            defaultProjectRef,
            defaultThreadEnvMode: envModeForProject(defaultProjectRef),
            handleNewThread,
          });
        },
      });
    }

    actionItems.push({
      kind: "submenu",
      value: "action:new-thread-in",
      searchTerms: ["new thread", "workspace", "project", "pick", "choose", "select"],
      title: "New thread in...",
      icon: <SquarePenIcon className={ITEM_ICON_CLASS} />,
      addonIcon: <SquarePenIcon className={ADDON_ICON_CLASS} />,
      groups: [{ value: "projects", label: "Workspaces", items: projectThreadItems }],
    });
  } else if (!isElectron) {
    actionItems.push({
      kind: "action",
      value: "action:new-thread",
      searchTerms: ["new thread", "chat", "create", "draft"],
      title: "New thread",
      icon: <SquarePenIcon className={ITEM_ICON_CLASS} />,
      shortcutCommand: "chat.new",
      run: async () => {
        await navigate({ to: "/new" });
      },
    });
  }

  function buildAddProjectSourceItem(
    source: AddProjectSourceDescriptor,
    lockedDestination: ProjectImportDestination | null = null,
  ): CommandPaletteActionItem {
    const item: Omit<CommandPaletteActionItem, "badge" | "disabled"> & {
      badge?: string;
      disabled?: boolean;
    } = {
      kind: "action",
      value: source.value,
      searchTerms: source.searchTerms,
      title: source.title,
      // A disabled row with no reason is a dead end. The reason takes the row's
      // own description slot so the user reads what to change, in place.
      description: source.disabledReason || source.description,
      icon: renderAddProjectSourceIcon(getAddProjectSourceIconName(source.id)),
      keepOpen: true,
      run: async () => {
        const mode = getAddProjectSourceMode(source.id);
        if (mode === "empty-project") {
          pushAddProjectSourceView("empty-project", EMPTY_PROJECT_ICON, lockedDestination);
        } else {
          pushAddProjectSourceView(
            mode,
            renderAddProjectSourceIcon(getAddProjectSourceIconName(source.id), ADDON_ICON_CLASS),
            lockedDestination,
          );
        }
      },
    };
    if (source.badge) item.badge = source.badge;
    if (source.disabled) item.disabled = true;
    return item;
  }

  // Real environments, not an abstract cloud/local toggle: the machine's own
  // name comes from the server (ServerEnvironmentLabel resolves the friendly
  // host label), and other connected machines come from the saved registry.
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((store) => store.byId);
  const addProjectDestinationChoices = useMemo(
    () =>
      buildProjectImportEnvironmentDescriptors({
        ...(runtimeSurface ? { surface: runtimeSurface } : {}),
        primary: primaryEnvironmentId
          ? {
              environmentId: primaryEnvironmentId,
              label: readPrimaryEnvironmentDescriptor()?.label ?? "",
            }
          : null,
        saved: Object.values(savedEnvironmentsById),
      }),
    [primaryEnvironmentId, runtimeSurface, savedEnvironmentsById],
  );
  // Environment rows for the root submenu. Each pushes the scoped source list
  // on top of itself, so Backspace returns here instead of closing the flow.
  const addProjectDestinationItems = useMemo<CommandPaletteActionItem[]>(
    () =>
      addProjectDestinationChoices.map((choice) => ({
        kind: "action",
        value: choice.value,
        searchTerms: choice.searchTerms,
        title: choice.title,
        description: choice.description,
        icon: renderAddProjectDestinationIcon(choice.destination),
        keepOpen: true,
        run: async () => {
          pushAddProjectSourcesView(importDestinationForEnvironmentRow(choice), true);
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addProjectDestinationChoices],
  );
  const addProjectDestinationGroup = useMemo<CommandPaletteGroup>(
    () => ({
      value: "environments",
      label: "Environments",
      items: addProjectDestinationItems,
    }),
    [addProjectDestinationItems],
  );
  const onlyAddProjectDestination =
    addProjectDestinationChoices.length === 1 ? addProjectDestinationChoices[0] : undefined;
  // With one possible destination there is nothing to ask, so the root menu
  // opens straight onto that destination's sources.
  const addProjectSourceItems = useMemo<CommandPaletteActionItem[]>(
    () =>
      buildAddProjectSourceDescriptors(
        onlyAddProjectDestination?.destination ?? null,
        runtimeSurface,
      ).map((source) =>
        buildAddProjectSourceItem(
          source,
          onlyAddProjectDestination
            ? importDestinationForEnvironmentRow(onlyAddProjectDestination)
            : null,
        ),
      ),
    [onlyAddProjectDestination, runtimeSurface],
  );

  useEffect(() => {
    if (!addProjectSourcesRequested) return;
    if (addProjectSourcesInitialMode === "cmmd-project") {
      pushAddProjectSourceView(
        "cmmd-project",
        renderAddProjectSourceIcon(getAddProjectSourceIconName("cmmd-project"), ADDON_ICON_CLASS),
        addProjectSourcesInitialDestination
          ? toProjectImportDestination(addProjectSourcesInitialDestination)
          : null,
      );
    } else if (addProjectSourcesInitialDestination === null) {
      // Generic open (root palette, desktop-cloud sidebar): nothing has answered
      // "where" yet, so ask that before showing any source.
      openAddProjectFlow();
    } else {
      pushAddProjectSourcesView(toProjectImportDestination(addProjectSourcesInitialDestination));
    }
    consumeAddProjectSourcesRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addProjectSourcesRequested,
    addProjectSourcesInitialDestination,
    addProjectSourcesInitialMode,
    consumeAddProjectSourcesRequest,
  ]);

  const addProjectRootDescriptor = buildAddProjectRootDescriptor();
  actionItems.push({
    kind: "submenu",
    value: "action:add-project",
    searchTerms: addProjectRootDescriptor.searchTerms,
    title: addProjectRootDescriptor.title,
    icon: <FolderPlusIcon className={ITEM_ICON_CLASS} />,
    addonIcon: <FolderPlusIcon className={ADDON_ICON_CLASS} />,
    groups: onlyAddProjectDestination
      ? [{ value: "sources", label: "Sources", items: addProjectSourceItems }]
      : [addProjectDestinationGroup],
  });

  actionItems.push({
    kind: "action",
    value: "action:settings",
    searchTerms: ["settings", "preferences", "configuration", "keybindings"],
    title: "Open settings",
    icon: <SettingsIcon className={ITEM_ICON_CLASS} />,
    run: async () => {
      await navigate({ to: "/settings" });
    },
  });

  const rootGroups = buildRootGroups({ actionItems, recentThreadItems });
  const activeGroups = currentView ? currentView.groups : rootGroups;

  const displayedGroups = filterCommandPaletteGroups({
    activeGroups,
    query: deferredQuery,
    isInSubmenu: currentView !== null,
    projectSearchItems: projectSearchItems,
    threadSearchItems: allThreadItems,
    cmmdTaskSearchItems: mergedCmmdTaskSearchItems,
  });

  const inputPlaceholder = getCommandPaletteInputPlaceholderForView({
    mode: paletteMode,
    currentView,
  });
  const isSubmenu = paletteMode === "submenu";
  // The value the CommandInput actually displays: the planning-context step
  // reads/writes `cmmdContextQuery` instead of the shared `query`.
  const activeQuery = isGitHubCmmdContextChoiceView ? cmmdContextQuery : query;

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.defaultPrevented) return;
    if (isEmptyProjectView && event.key === "Enter") {
      event.preventDefault();
      void createEmptyProject();
      return;
    }
    if (event.key === "Backspace" && activeQuery === "" && isSubmenu) {
      event.preventDefault();
      popView();
    }
  }

  function executeItem(item: CommandPaletteActionItem | CommandPaletteSubmenuItem): void {
    if (item.kind === "submenu") {
      pushView(item);
      return;
    }

    if (!item.keepOpen) {
      setOpen(false);
    }

    void item.run().catch((error: unknown) => {
      toastManager.add({
        type: "error",
        title: "Unable to run command",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
      });
    });
  }

  return (
    <CommandDialogPopup
      aria-label="Command palette"
      className="overflow-hidden p-0"
      data-testid="command-palette"
      finalFocus={() => {
        composerHandleRef?.current?.focusAtEnd();
        return false;
      }}
    >
      <Command
        key={viewStack.length}
        aria-label="Command palette"
        autoHighlight="always"
        mode="none"
        onValueChange={handleQueryChange}
        value={activeQuery}
      >
        <div className="relative">
          <CommandInput
            className={isEmptyProjectView ? "pe-36" : undefined}
            placeholder={inputPlaceholder}
            wrapperClassName={
              isSubmenu
                ? "[&_[data-slot=autocomplete-start-addon]]:pointer-events-auto [&_[data-slot=autocomplete-start-addon]]:cursor-pointer"
                : undefined
            }
            {...(isSubmenu
              ? {
                  startAddon: (
                    <button
                      type="button"
                      className="flex cursor-pointer items-center"
                      aria-label="Back"
                      onClick={popView}
                    >
                      <ArrowLeftIcon />
                    </button>
                  ),
                }
              : {})}
            onKeyDown={handleKeyDown}
            onKeyDownCapture={isEmptyProjectView ? handleKeyDown : undefined}
          />
          {projectSourceSubmitState.visible ? (
            <Button
              variant="outline"
              size="xs"
              tabIndex={-1}
              className="absolute inset-e-2.5 top-1/2 gap-1.5 pe-1 ps-2 -translate-y-1/2"
              disabled={projectSourceSubmitState.disabled}
              aria-label={projectSourceSubmitState.ariaLabel}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => void createEmptyProject()}
              title={projectSourceSubmitState.title}
            >
              <span>{getProjectSourceSubmitLabel({ isCreatingEmptyProject, isAddingSource })}</span>
              <KbdGroup className="pointer-events-none -me-0.5 items-center gap-1">
                <Kbd>
                  {navigator.platform.toLowerCase().includes("mac") ? "⌘ Enter" : "Ctrl Enter"}
                </Kbd>
              </KbdGroup>
            </Button>
          ) : null}
        </div>
        {isEmptyProjectView ? (
          <CommandPaletteEmptyProjectPanel
            allowedDestinations={getAllowedProjectImportDestinations(
              "empty-project",
              projectImportLockedDestination?.kind ?? null,
              runtimeSurface,
            )}
            onSelectDestination={selectProjectImportDestination}
            selectedDestination={currentProjectImportKind}
          />
        ) : isCmmdProjectView ? (
          <CommandPanel className="flex min-h-[260px] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-b-none">
            <ProjectImportDestinationPicker
              allowedDestinations={getAllowedProjectImportDestinations(
                "cmmd-project",
                projectImportLockedDestination?.kind ?? null,
                runtimeSurface,
              )}
              onSelect={selectProjectImportDestination}
              selectedDestination={currentProjectImportKind}
            />
            <CommandPaletteSourceList ariaLabel="CMMD projects" navigable>
              {cmmdProjectListState.kind === "loading" ? (
                <div className="flex items-center gap-2 px-3 py-8 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" />
                  Loading CMMD projects...
                </div>
              ) : cmmdProjectListState.kind === "error" ? (
                <div className="space-y-3 px-3 py-8 text-sm text-muted-foreground">
                  <p>Couldn&apos;t load your CMMD projects.</p>
                  <Button
                    size="sm"
                    onClick={() => cmmdWorkIntakeResource.requestRefetch({ authoritative: true })}
                  >
                    Retry
                  </Button>
                </div>
              ) : cmmdProjectListState.kind === "empty" ? (
                <div className="px-3 py-8 text-sm text-muted-foreground">
                  {cmmdProjectListState.reason === "no-projects"
                    ? "You don’t have any CMMD projects yet."
                    : `No projects match “${query}”.`}
                </div>
              ) : (
                <CmmdProjectImportRows
                  isRefreshing={cmmdProjectListState.isRefreshing}
                  disabled={isAddingSource}
                  groups={cmmdProjectWorkspaceGroups}
                  searchActive={deferredQuery.trim() !== ""}
                  onSelectProject={(project) => void createFromCmmdProject(project)}
                />
              )}
            </CommandPaletteSourceList>
          </CommandPanel>
        ) : isGitHubCmmdContextChoiceView && pendingGitHubCmmdContextChoice ? (
          <CommandPanel className="flex min-h-[260px] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-b-none">
            <div className="border-b border-border/60 px-5 py-4">
              <p className="text-xs font-medium tracking-[0.08em] text-muted-foreground/60 uppercase">
                CMMD planning context
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Attach{" "}
                <span className="font-medium text-foreground">
                  {pendingGitHubCmmdContextChoice.repo.full_name}
                </span>{" "}
                to a CMMD project
                {canSkipCmmdPlanningContext(pendingGitHubCmmdContextChoice.destination)
                  ? ", or skip planning context."
                  : " — required to run on a Forge Cloud VM."}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground">
                  <CloudIcon className="size-3.5" />
                  <span>
                    {projectImportDestinationLabel(pendingGitHubCmmdContextChoice.destination)}
                  </span>
                </div>
                <div className="inline-flex items-center rounded-md border border-border/70 bg-muted/30 p-0.5 text-xs">
                  {(["link", "create"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setGithubPlanningMode(mode)}
                      className={`rounded-[5px] px-2.5 py-1 font-medium transition-colors ${
                        githubPlanningMode === mode
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {mode === "link" ? "Link existing" : "Create new"}
                    </button>
                  ))}
                </div>
                {canSkipCmmdPlanningContext(pendingGitHubCmmdContextChoice.destination) ? (
                  <Button
                    disabled={isAddingSource}
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void cloneFromGitHub(pendingGitHubCmmdContextChoice.repo, null, {
                        skipCmmdContext: true,
                      })
                    }
                  >
                    Skip
                  </Button>
                ) : null}
              </div>
            </div>
            {githubPlanningMode === "link" ? (
              <CommandPaletteSourceList ariaLabel="CMMD projects" navigable>
                {cmmdContextListState.kind === "loading" ? (
                  <div className="space-y-3 p-3">
                    <div className="flex items-center gap-2 px-2 text-sm text-muted-foreground">
                      <Loader2Icon className="size-4 animate-spin" />
                      Loading CMMD projects...
                    </div>
                    {[
                      "cmmd-project-skeleton-1",
                      "cmmd-project-skeleton-2",
                      "cmmd-project-skeleton-3",
                    ].map((key) => (
                      <Skeleton key={key} className="h-12 rounded-md" />
                    ))}
                  </div>
                ) : cmmdContextListState.kind === "error" ? (
                  <div className="space-y-3 px-3 py-8 text-sm text-muted-foreground">
                    <p>Couldn&apos;t load your CMMD projects.</p>
                    <Button
                      size="sm"
                      onClick={() => cmmdWorkIntakeResource.requestRefetch({ authoritative: true })}
                    >
                      Retry
                    </Button>
                  </div>
                ) : cmmdContextListState.kind === "empty" &&
                  cmmdContextListState.reason === "no-projects" ? (
                  <div className="space-y-3 px-3 py-8 text-sm text-muted-foreground">
                    <p>You don&apos;t have any CMMD projects yet.</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGithubPlanningMode("create")}
                    >
                      Create new project instead
                    </Button>
                  </div>
                ) : cmmdContextListState.kind === "empty" ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">
                    No projects match &quot;{cmmdContextQuery}&quot;.
                  </div>
                ) : (
                  <CmmdProjectImportGroups
                    variant="link"
                    idPrefix="planning-context"
                    disabled={isAddingSource}
                    groups={gitHubCmmdProjectWorkspaceGroups}
                    searchActive={deferredCmmdContextQuery.trim() !== ""}
                    getRowLabel={(project) =>
                      `Link ${project.title} to ${pendingGitHubCmmdContextChoice.repo.full_name}`
                    }
                    onSelectProject={(project) =>
                      void cloneFromGitHubExistingCmmdProject(
                        pendingGitHubCmmdContextChoice.repo,
                        project,
                      )
                    }
                  />
                )}
              </CommandPaletteSourceList>
            ) : (
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <div className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground/60">
                      Project name
                    </span>
                    <input
                      value={githubCreateProjectName}
                      onChange={(event) => setGithubCreateProjectName(event.target.value)}
                      placeholder="New CMMD project name"
                      className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground/60">
                      Workspace
                    </span>
                    <select
                      className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
                      value={String(parsePositiveCmmdId(selectedGitHubCmmdWorkspace?.id) ?? "")}
                      onChange={(event) =>
                        setPendingGitHubCmmdWorkspaceId(parsePositiveCmmdId(event.target.value))
                      }
                    >
                      {cmmdWorkspaces.map((workspace) => (
                        <option key={workspace.id} value={String(workspace.id)}>
                          {workspace.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    className="w-full"
                    disabled={
                      isAddingSource ||
                      cmmdWorkspaces.length === 0 ||
                      githubCreateProjectName.trim().length === 0
                    }
                    onClick={() =>
                      void createGitHubCmmdProjectAndClone(pendingGitHubCmmdContextChoice.repo)
                    }
                  >
                    <FolderPlusIcon className="size-4" />
                    <span>Create project &amp; clone</span>
                  </Button>
                </div>
              </div>
            )}
          </CommandPanel>
        ) : isGitHubView ? (
          <CommandPanel className="flex min-h-[260px] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-b-none">
            <ProjectSourcePanelError error={githubError} />
            <ProjectImportDestinationPicker
              allowedDestinations={getAllowedProjectImportDestinations(
                "github",
                projectImportLockedDestination?.kind ?? null,
                runtimeSurface,
              )}
              onSelect={selectProjectImportDestination}
              selectedDestination={currentProjectImportKind}
            />
            {requiresCmmdAuth ? (
              <div className="space-y-3 px-5 py-8 text-sm text-muted-foreground">
                <p>Your CMMD session expired. Sign back in before using GitHub repositories.</p>
                <Button size="sm" onClick={() => void reconnectCmmd()}>
                  Sign in with CMMD
                </Button>
              </div>
            ) : githubLoading ? (
              <div className="space-y-3 p-3">
                <div className="flex items-center gap-2 px-2 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" />
                  Loading repositories...
                </div>
                {["repo-1", "repo-2", "repo-3", "repo-4"].map((key) => (
                  <Skeleton key={key} className="h-12 rounded-md" />
                ))}
              </div>
            ) : !github_connected ? (
              <div className="space-y-3 px-5 py-8 text-sm text-muted-foreground">
                <p>Connect GitHub in CMMD before cloning repositories.</p>
                <Button size="sm" onClick={() => void connectGitHub()}>
                  Connect GitHub
                </Button>
              </div>
            ) : githubRepos.length === 0 ? (
              <div className="px-5 py-8 text-sm text-muted-foreground">No repositories found.</div>
            ) : (
              <>
                {githubWarnings.length > 0 ? (
                  <div className="px-5 pt-3">
                    <div className="flex flex-col gap-2 rounded-md border border-warning-border bg-warning-surface px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="min-w-0 text-xs font-medium text-warning">
                        {githubWarnings[0]}
                      </p>
                      <button
                        type="button"
                        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-warning-border bg-background px-2.5 text-xs font-semibold text-foreground shadow-xs transition-colors hover:bg-secondary"
                        onClick={() => void connectGitHub()}
                      >
                        <GithubIcon className="size-3.5" />
                        Reconnect GitHub
                      </button>
                    </div>
                  </div>
                ) : null}
                {filteredGithubRepos.length === 0 ? (
                  <div className="px-5 py-8 text-sm text-muted-foreground">
                    No repositories match &quot;{query}&quot;.
                  </div>
                ) : (
                  <CommandPaletteSourceList ariaLabel="GitHub repositories">
                    {filteredGithubRepos.map((repo) => {
                      const importedProject = importedGithubProjectByRepo.get(repo.full_name);
                      const isImporting = activeProjectImportJobs.some(
                        (job) =>
                          job.id ===
                          githubProjectImportJobId(repo.full_name, currentProjectImportKind),
                      );
                      return (
                        <li key={repo.full_name}>
                          <button
                            type="button"
                            className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-md px-3 py-2.5 text-left hover:bg-accent/70 disabled:opacity-50"
                            disabled={isImporting}
                            onClick={() => handleGitHubRepoSelected(repo)}
                          >
                            <span className="min-w-0 space-y-1">
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="truncate text-sm font-semibold">
                                  {repo.full_name}
                                </span>
                                {repo.private ? (
                                  <LockIcon className="size-3 text-muted-foreground/70" />
                                ) : null}
                              </span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {[
                                  getGitHubRepoAccessLabel(repo),
                                  repo.language,
                                  repo.updated_at ? formatRelativeTimeLabel(repo.updated_at) : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </span>
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 self-center rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground/80",
                                importedProject ? "border-success-border text-success" : null,
                              )}
                            >
                              {isImporting ? (
                                <>
                                  <Loader2Icon className="size-3 animate-spin" />
                                  Cloning...
                                </>
                              ) : importedProject ? (
                                `Open ${projectImportDestinationLabel(currentProjectImportKind)}`
                              ) : (
                                "Clone"
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </CommandPaletteSourceList>
                )}
              </>
            )}
          </CommandPanel>
        ) : isLocalFolderView ? (
          <CommandPaletteLocalFolderPanel
            isAddingSource={isAddingSource}
            onChooseFolder={() => void addFromLocalFolder()}
          />
        ) : (
          <CommandPanel className="max-h-[min(28rem,70vh)]">
            <CommandPaletteResults
              groups={displayedGroups}
              isActionsOnly={isActionsOnly}
              keybindings={keybindings}
              onExecuteItem={executeItem}
            />
          </CommandPanel>
        )}
        <CommandPaletteFooterHints isSubmenu={isSubmenu} />
      </Command>
    </CommandDialogPopup>
  );
}

async function listCmmdProjectContextLinksForGitHubRepo(
  repoFullName: string,
): Promise<readonly CmmdProjectContextLink[]> {
  try {
    return await cmmdApiClient.listProjectContextLinks({ githubRepoFullName: repoFullName });
  } catch (error) {
    throw new Error(
      error instanceof Error && error.message.trim().length > 0
        ? `Could not check CMMD planning contexts for ${repoFullName}: ${error.message}`
        : `Could not check CMMD planning contexts for ${repoFullName}.`,
      { cause: error },
    );
  }
}

/**
 * Report a source-step outcome on the shared toast, like the rest of the app.
 *
 * `blocked` is a precondition the user can satisfy (connect GitHub, pick a
 * workspace) — nothing failed, so it must not borrow destructive styling.
 * `error` is work that actually went wrong. Keeping them apart is what stops
 * red from becoming background noise.
 */
function reportSourceOutcome(tone: "blocked" | "error", title: string, description: string): void {
  toastManager.add({ type: tone === "blocked" ? "info" : "error", title, description });
}

// On-demand GitHub login resolver — used so a CMMD-project import isn't blocked
// by the async race where github_login (from fetchStatus) hasn't populated yet.
async function resolveGithubLoginNow(): Promise<string> {
  try {
    const url = resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/status");
    const response = await fetch(url, buildPrimaryFetchInit({ credentials: "include" }));
    if (!response.ok) return "";
    const data = (await response.json().catch(() => ({}))) as { readonly github_login?: string };
    return data.github_login?.trim() ?? "";
  } catch {
    return "";
  }
}

async function reconnectCmmd(): Promise<void> {
  await logoutPrimarySession().catch(() => undefined);
  window.location.href = resolvePrimaryEnvironmentHttpUrl("/login");
}
